import * as Yup from "yup";

const uuidPattern =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const userFormValidationByRole: any = () => {
  return Yup.object().shape({
    // Required static fields for all roles
    firstName: Yup.string()
      .required("First Name is required")
      .min(1, "First Name must be at least 1 characters")
      .max(50, "First Name must not exceed 50 characters")
      .trim(),
    lastName: Yup.string()
      .required("Last Name is required")
      .min(1, "Last Name must be at least 1 characters")
      .max(50, "Last Name must not exceed 50 characters")
      .trim(),
    email: Yup.string()
      .email("Invalid email format")
      .required("Email is required")
      .trim(),
    phone: Yup.string()
      .required("Phone is required")
      .matches(/^\d{10}$/, "Phone must be exactly 10 digits")
      .trim(),
    roleId: Yup.string()
      .required("Role is required")
      .matches(uuidPattern, "Role must be a valid UUID"),
    roleName: Yup.string(),
    status: Yup.boolean(),
    roles: Yup.array(),

    // Height - required for coach, scout, player
    height: Yup.string().when("roleName", {
      is: (roleName: string) => ["coach", "scout", "player"].includes(roleName),
      then: (schema) =>
        schema
          .required("Height is required")
          .typeError("Height must be a number")
          .test(
            "valid-height",
            "Height must be a positive number",
            function (value) {
              const num = parseFloat(value);
              return !isNaN(num) && num > 0;
            },
          ),
      otherwise: (schema) => schema,
    }),

    // Weight - required for coach, scout, player
    weight: Yup.string().when("roleName", {
      is: (roleName: string) => ["coach", "scout", "player"].includes(roleName),
      then: (schema) =>
        schema
          .required("Weight is required")
          .typeError("Weight must be a number")
          .test(
            "valid-weight",
            "Weight must be a positive number",
            function (value) {
              const num = parseFloat(value);
              return !isNaN(num) && num > 0;
            },
          ),
      otherwise: (schema) => schema,
    }),

    // Date of Birth - required for coach, scout, player
    dateOfBirth: Yup.string().when("roleName", {
      is: (roleName: string) => ["coach", "scout", "player"].includes(roleName),
      then: (schema) =>
        schema
          .required("Date of Birth is required")
          .typeError("Date of Birth must be a valid date")
          .test(
            "valid-dob",
            "Date of Birth must be a valid date",
            function (value) {
              const date = new Date(value);
              return !isNaN(date.getTime());
            },
          )
          .test(
            "not-future",
            "Date of Birth cannot be in the future",
            function (value) {
              const date = new Date(value);
              return date < new Date();
            },
          ),
      otherwise: (schema) => schema,
    }),

    // Sports - required for coach, scout, player
    sports: Yup.string().when("roleName", {
      is: (roleName: string) => ["coach", "scout", "player"].includes(roleName),
      then: (schema) =>
        schema
          .required("Sports is required")
          .test("valid-uuid", "Sports must be a valid UUID", function (value) {
            return uuidPattern.test(value);
          }),
      otherwise: (schema) => schema,
    }),

    // Position - required for coach, scout, player
    position: Yup.string().when("roleName", {
      is: (roleName: string) => ["coach", "scout", "player"].includes(roleName),
      then: (schema) =>
        schema
          .required("Position is required")
          .test(
            "valid-uuid",
            "Position must be a valid UUID",
            function (value) {
              return uuidPattern.test(value);
            },
          ),
      otherwise: (schema) => schema,
    }),

    // Preferred Position - required for coach, scout, player
    preferredPosition: Yup.string().when("roleName", {
      is: (roleName: string) => ["coach", "scout", "player"].includes(roleName),
      then: (schema) =>
        schema
          .required("Preferred Position is required")
          .max(50, "Preferred Position must not exceed 50 characters"),
      otherwise: (schema) => schema,
    }),

    // Dominant Foot - required for coach, scout, player
    dominantFoot: Yup.string().when("roleName", {
      is: (roleName: string) => ["coach", "scout", "player"].includes(roleName),
      then: (schema) =>
        schema
          .required("Dominant Foot is required")
          .test(
            "valid-foot",
            "Dominant Foot must be either Left or Right",
            function (value) {
              return ["left", "right"].includes(value.toLowerCase());
            },
          ),
      otherwise: (schema) => schema,
    }),

    // Skills - required for coach, scout, player
    skills: Yup.string().when("roleName", {
      is: (roleName: string) => ["coach", "scout", "player"].includes(roleName),
      then: (schema) =>
        schema
          .required("Skills is required")
          .max(500, "Skills description must not exceed 500 characters"),
      otherwise: (schema) => schema,
    }),

    // City - required for coach, scout, player
    city: Yup.string().when("roleName", {
      is: (roleName: string) => ["coach", "scout", "player"].includes(roleName),
      then: (schema) =>
        schema
          .required("City is required")
          .max(50, "City must not exceed 50 characters"),
      otherwise: (schema) => schema,
    }),

    // State - required for coach, scout, player
    state: Yup.string().when("roleName", {
      is: (roleName: string) => ["coach", "scout", "player"].includes(roleName),
      then: (schema) =>
        schema
          .required("State is required")
          .max(50, "State must not exceed 50 characters"),
      otherwise: (schema) => schema,
    }),

    // Nationality - required for coach, scout, player
    nationality: Yup.string().when("roleName", {
      is: (roleName: string) => ["coach", "scout", "player"].includes(roleName),
      then: (schema) =>
        schema
          .required("Nationality is required")
          .max(50, "Nationality must not exceed 50 characters"),
      otherwise: (schema) => schema,
    }),

    // Club - required for coach, scout; optional for player
    clubId: Yup.string().when("roleName", {
      is: (roleName: string) => ["coach", "scout"].includes(roleName),
      then: (schema) => schema.required("Club is required"),
      otherwise: (schema) => schema.nullable(),
    }),

    // League - required for coach, scout; optional for player
    leagueId: Yup.string().when("roleName", {
      is: (roleName: string) => ["coach", "scout"].includes(roleName),
      then: (schema) => schema.required("League is required"),
      otherwise: (schema) => schema.nullable(),
    }),

    // Description - required for player only
    description: Yup.string().when("roleName", {
      is: (roleName: string) => roleName === "player",
      then: (schema) =>
        schema
          .required("Description is required")
          .max(1000, "Description must not exceed 1000 characters"),
      otherwise: (schema) =>
        schema.max(1000, "Description must not exceed 1000 characters"),
    }),

    // Country - optional for all roles
    country: Yup.string().max(50, "Country must not exceed 50 characters"),
  });
};
